import * as React from "react";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import styled, { css } from "styled-components";
import chroma from "chroma-js";
import { makeVisualization, retrieveChartData } from "views/components/aggregationbuilder/AggregationBuilder";
import useMapKeys from "views/components/visualizations/useMapKeys";
import extractLeafPaths from "views/components/visualizations/utils/extractLeafPaths";
import usePlotOnClickPopover from "views/components/visualizations/hooks/usePlotOnClickPopover";
import sankeyOnClickPopover from "views/components/visualizations/sankey/sankeyOnClickPopover";
import { defaultChartColors } from "views/components/visualizations/Colors";
import { SANKEY_VISUALIZATION_TYPE } from "./Constants";
import GenericPlot from "../GenericPlot";
const Container = styled.div`
  height: 100%;
  width: 100%;
`;
const EmptyState = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    padding: ${theme.spacings.md};
    color: ${theme.colors.text.secondary};
    text-align: center;
  `
);
const STAGE_SEPARATOR = " ";
const NODE_ALPHA = 1;
const LINK_ALPHA = 0.3;
const LINK_HOVER_ALPHA = 0.6;
const paletteColor = (index, alpha) => chroma(defaultChartColors[index % defaultChartColors.length]).alpha(alpha).hex("rgba");
const buildSankeyTrace = (paths, displayKeys, allFields) => {
  const nodeIndex = /* @__PURE__ */ new Map();
  const labels = [];
  const customdata = [];
  const indexOf = (stage, label2, originalValue) => {
    const k = `${stage}${STAGE_SEPARATOR}${String(originalValue)}`;
    const existing = nodeIndex.get(k);
    if (existing !== void 0) return existing;
    const idx = labels.length;
    nodeIndex.set(k, idx);
    labels.push(label2);
    customdata.push({ field: allFields[stage], value: originalValue });
    return idx;
  };
  const linkAgg = /* @__PURE__ */ new Map();
  paths.forEach((path, pathIdx) => {
    const displayed = displayKeys[pathIdx];
    for (let i = 0; i < displayed.length - 1; i += 1) {
      const source2 = indexOf(i, displayed[i], path.keys[i]);
      const target2 = indexOf(i + 1, displayed[i + 1], path.keys[i + 1]);
      const linkKey = `${source2}${STAGE_SEPARATOR}${target2}`;
      const existing = linkAgg.get(linkKey);
      if (existing) {
        existing.value += path.value;
      } else {
        linkAgg.set(linkKey, { source: source2, target: target2, value: path.value });
      }
    }
  });
  const source = [];
  const target = [];
  const value = [];
  const label = [];
  linkAgg.forEach(({ source: s, target: t, value: v }) => {
    source.push(s);
    target.push(t);
    value.push(v);
    label.push(`${labels[s]} \u2192 ${labels[t]}`);
  });
  return {
    type: SANKEY_VISUALIZATION_TYPE,
    orientation: "h",
    arrangement: "fixed",
    node: {
      label: labels,
      customdata,
      color: labels.map((_, i) => paletteColor(i, NODE_ALPHA)),
      pad: 15,
      thickness: 18
    },
    link: {
      source,
      target,
      value,
      label,
      color: target.map((t) => paletteColor(t, LINK_ALPHA)),
      hovercolor: target.map((t) => paletteColor(t, LINK_HOVER_ALPHA))
    }
  };
};
const layout = { margin: { t: 20, b: 20, l: 20, r: 20 } };
const SankeyVisualization = makeVisualization(({ config, data }) => {
  const { t } = useTranslation("aggregation");
  const rows = retrieveChartData(data);
  const mapKeys = useMapKeys();
  const { onChartClick, initializeGraphDivRef, popover } = usePlotOnClickPopover({ ...sankeyOnClickPopover, config });
  const trace = useMemo(() => {
    var _a;
    const rowFields = config.rowPivots.flatMap((pivot) => pivot.fields);
    const columnFields = config.columnPivots.flatMap((pivot) => pivot.fields);
    const allFields = [...rowFields, ...columnFields];
    if (allFields.length < 2 || !rows) {
      return null;
    }
    const metric = (_a = config.series) == null ? void 0 : _a[0];
    const paths = extractLeafPaths(rows, columnFields.length, metric == null ? void 0 : metric.effectiveName);
    if (paths.length === 0) {
      return null;
    }
    const displayKeys = paths.map((path) => path.keys.map((k, i) => {
      var _a2;
      return String((_a2 = mapKeys(k, allFields[i])) != null ? _a2 : k);
    }));
    return buildSankeyTrace(paths, displayKeys, allFields);
  }, [config, mapKeys, rows]);
  return /* @__PURE__ */ React.createElement(Container, null, trace ? /* @__PURE__ */ React.createElement(
    GenericPlot,
    {
      chartData: [trace],
      layout,
      onClickMarker: onChartClick,
      onInitialized: initializeGraphDivRef
    }
  ) : /* @__PURE__ */ React.createElement(EmptyState, null, t("vis_sankey.no_flows")), popover);
}, SANKEY_VISUALIZATION_TYPE);
SankeyVisualization.displayName = "SankeyVisualization";
export default SankeyVisualization;
